import { Link } from 'react-router-dom';
import { getUserLanguage } from '../../utils/language';
import useSettings from '../../hooks/useSettings';
import { IMAGE_BASE_URL } from '../../config';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const language = getUserLanguage();
  const { settings, error: settingsError } = useSettings();

  if (settingsError) {
    console.error('[Footer] Settings fetch error:', settingsError.message, settingsError.stack);
  }

  const socialLinks = {
    instagram: settings.instagram_url?.translations?.[language]?.setting_value || 'https://www.instagram.com/norisknodrift/',
    facebook: settings.facebook_url?.translations?.[language]?.setting_value || 'https://www.facebook.com/NoRiskNoDrift/',
    youtube: settings.youtube_url?.translations?.[language]?.setting_value || 'https://www.youtube.com/@norisknodrift',
    tiktok: settings.tiktok_url?.translations?.[language]?.setting_value || 'https://www.tiktok.com/@norisknodrift',
  };

  const texts = {
    cs: {
      copyright: `© ${currentYear} NO RISK NO DRIFT. Všechna práva vyhrazena.`,
      team: 'Team',
      cars: 'Auta',
      events: 'Events',
      gallery: 'Galerie',
      sponsors: 'Sponzoři',
      shop: 'E-shop',
      driftTaxi: 'Drift Taxi',
      service: 'Servis',
      terms: 'Podmínky užití',
      privacy: 'Zásady ochrany osobních údajů',
      cookies: 'Nastavení cookies',
      credits: 'Design a vývoj od týmu NO RISK NO DRIFT IT',
    },
    en: {
      copyright: `© ${currentYear} NO RISK NO DRIFT. All rights reserved.`,
      team: 'Team',
      cars: 'Cars',
      events: 'Events',
      gallery: 'Gallery',
      sponsors: 'Sponsors',
      shop: 'Shop',
      driftTaxi: 'Drift Taxi',
      service: 'Service',
      terms: 'Terms and Conditions',
      privacy: 'Privacy Policy',
      cookies: 'Cookie Settings',
      credits: 'Design & development by NO RISK NO DRIFT IT Team',
    },
  };

  const currentText = texts[language] || texts.en;

  return (
    <footer className="bg-black py-10 border-t border-gray-800">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="flex flex-col md:flex-row justify-between items-center">
          <div className="mb-6 md:mb-0">
            <Link to="/#home">
            <img
              src={`${IMAGE_BASE_URL}/images/logo-A2.png`}
              alt="NO RISK NO DRIFT Logo"
              className="h-10 w-auto"
            />
            </Link>
            <p className="text-gray-500 text-sm mt-2">{currentText.copyright}</p>
          </div>

          <div className="flex flex-wrap justify-center gap-6">
            <Link to="/#team" className="text-gray-400 hover:text-red-500 text-sm transition-colors">
              {currentText.team}
            </Link>
            <Link to="/#cars" className="text-gray-400 hover:text-red-500 text-sm transition-colors">
              {currentText.cars}
            </Link>
            <Link to="/#events" className="text-gray-400 hover:text-red-500 text-sm transition-colors">
              {currentText.events}
            </Link>
            <Link to="/#gallery" className="text-gray-400 hover:text-red-500 text-sm transition-colors">
              {currentText.gallery}
            </Link>
            <Link to="/#sponsors" className="text-gray-400 hover:text-red-500 text-sm transition-colors">
              {currentText.sponsors}
            </Link>
            <Link to="/#shop" className="text-gray-400 hover:text-red-500 text-sm transition-colors">
              {currentText.shop}
            </Link>
            <Link to="/#drift-taxi" className="text-gray-400 hover:text-red-500 text-sm transition-colors">
              {currentText.driftTaxi}
            </Link>
            <a
              href="https://noriskservis.cz"
              className="text-gray-400 hover:text-red-500 text-sm transition-colors"
              target="_blank"
              rel="noopener noreferrer"
            >
              {currentText.service}
            </a>
          </div>

          <div className="flex space-x-4 mt-6 md:mt-0">
            {socialLinks.instagram && (
              <a
                href={socialLinks.instagram}
                className="text-gray-400 hover:text-red-500 transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fab fa-instagram text-xl"></i>
              </a>
            )}
            {socialLinks.facebook && (
              <a
                href={socialLinks.facebook}
                className="text-gray-400 hover:text-red-500 transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fab fa-facebook-f text-xl"></i>
              </a>
            )}
            {socialLinks.youtube && (
              <a
                href={socialLinks.youtube}
                className="text-gray-400 hover:text-red-500 transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fab fa-youtube text-xl"></i>
              </a>
            )}
            {socialLinks.tiktok && (
              <a
                href={socialLinks.tiktok}
                className="text-gray-400 hover:text-red-500 transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fab fa-tiktok text-xl"></i>
              </a>
            )}
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-gray-800 text-center">
          <div className="flex flex-wrap justify-center gap-4 mb-4">
            <Link to="/terms" className="text-gray-400 hover:text-red-500 text-sm transition-colors">
              {currentText.terms}
            </Link>
            <Link to="/privacy" className="text-gray-400 hover:text-red-500 text-sm transition-colors">
              {currentText.privacy}
            </Link>
            <Link to="/cookies" className="text-gray-400 hover:text-red-500 text-sm transition-colors">
              {currentText.cookies}
            </Link>
          </div>
          <p className="text-gray-500 text-xs">
            {currentText.credits}{' '}
            <a href={`mailto:${settings.contact_email?.translations?.[language]?.setting_value || 'info@norisknodrift.com'}`} className="text-red-500 hover:text-red-400">
              {settings.contact_email?.translations?.[language]?.setting_value || 'info@norisknodrift.com'}
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;